import {Fragment, useState} from "react";
import {
    Box,
    Divider,
    IconButton,
    Link,
    Stack,
    Tooltip,
    Typography,
    useTheme,
} from "@mui/material";
import {NotificationResponse} from "../data/NotificationResponse";
import {Link as RouterLink} from "react-router-dom";
import {
    Delete,
    MarkAsUnread,
    MarkAsUnreadOutlined,
    NotificationsNone,
    Event as EventIcon,
    Gavel,
    ContactSupport,
    Campaign,
    Person,
    WorkOutline,
    FiberManualRecord
} from "@mui/icons-material";
import {LoadingContent} from "./LoadingContent";

function getNotificationIcon(link: string) {
    if (link.includes("/event")) return <EventIcon sx={{fontSize: 16}}/>;
    if (link.includes("/internal-affairs") || link.includes("/complaint")) return <Gavel sx={{fontSize: 16}}/>;
    if (link.includes("/helpdesk")) return <ContactSupport sx={{fontSize: 16}}/>;
    if (link.includes("/announcement")) return <Campaign sx={{fontSize: 16}}/>;
    if (link.includes("/user")) return <Person sx={{fontSize: 16}}/>;
    if (link.includes("/careers")) return <WorkOutline sx={{fontSize: 16}}/>;
    return <NotificationsNone sx={{fontSize: 16}}/>;
}

function groupNotificationsByTime(notifications: NotificationResponse[]): {today: NotificationResponse[], earlier: NotificationResponse[]} {
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    const today: NotificationResponse[] = [];
    const earlier: NotificationResponse[] = [];

    notifications.forEach(n => {
        const date = new Date(n.date);
        if (date >= startOfToday) {
            today.push(n);
        } else {
            earlier.push(n);
        }
    });

    return {today, earlier};
}

function formatRelativeTime(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString(window.navigator.language, {
        month: "short",
        day: "numeric"
    });
}

interface NotificationItemProps {
    notification: NotificationResponse;
    onMarkRead: (id: number, read: boolean) => void;
    onDelete: (id: number) => void;
    onClose?: () => void;
}

const NotificationItem = (props: NotificationItemProps) => {
    const {notification} = props;
    const [hovered, setHovered] = useState(false);
    const theme = useTheme();

    return (
        <Link
            component={RouterLink}
            to={notification.link}
            underline="none"
            color="inherit"
            onClick={() => {
                if (!notification.read) {
                    props.onMarkRead(notification.id, true);
                }
                props.onClose?.();
            }}
        >
            <Stack
                direction="row"
                spacing={1.5}
                alignItems="flex-start"
                sx={{
                    px: 2,
                    py: 1.5,
                    transition: "background 0.15s ease",
                    "&:hover": {background: theme.palette.action.hover},
                    position: "relative"
                }}
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
            >
                <Box sx={{
                    color: notification.read ? theme.palette.text.disabled : theme.palette.text.secondary,
                    mt: 0.25,
                    display: "flex",
                    alignItems: "center"
                }}>
                    {getNotificationIcon(notification.link)}
                </Box>

                <Stack spacing={0.25} sx={{flex: 1, minWidth: 0, pr: hovered ? 6 : 0}}>
                    <Typography
                        variant="body2"
                        sx={{
                            fontWeight: notification.read ? 400 : 500,
                            lineHeight: 1.4,
                            color: notification.read ? theme.palette.text.secondary : theme.palette.text.primary,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical"
                        }}
                    >
                        {notification.message}
                    </Typography>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                        {!notification.read && (
                            <FiberManualRecord sx={{fontSize: 8, color: theme.palette.secondary.main}}/>
                        )}
                        <Typography
                            variant="caption"
                            color="text.disabled"
                        >
                            {formatRelativeTime(notification.date)}
                        </Typography>
                    </Stack>
                </Stack>

                {hovered && (
                    <Stack
                        direction="row"
                        spacing={0}
                        sx={{
                            position: "absolute",
                            right: 8,
                            top: "50%",
                            transform: "translateY(-50%)",
                            bgcolor: theme.palette.background.paper,
                            borderRadius: 1,
                            border: `1px solid ${theme.palette.divider}`
                        }}
                    >
                        <Tooltip arrow title={notification.read ? "Mark unread" : "Mark read"}>
                            <IconButton
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    props.onMarkRead(notification.id, !notification.read);
                                }}
                            >
                                {notification.read ? <MarkAsUnread fontSize="small"/> : <MarkAsUnreadOutlined fontSize="small"/>}
                            </IconButton>
                        </Tooltip>
                        <Tooltip arrow title="Delete">
                            <IconButton
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    props.onDelete(notification.id);
                                }}
                            >
                                <Delete fontSize="small"/>
                            </IconButton>
                        </Tooltip>
                    </Stack>
                )}
            </Stack>
        </Link>
    );
};

interface NotificationPanelProps {
    notifications: NotificationResponse[];
    isLoading: boolean;
    onMarkRead: (id: number, read: boolean) => void;
    onDelete: (id: number) => void;
    onReadAll: () => void;
    onClose?: () => void;
    compact?: boolean;
}

export const NotificationPanel = (props: NotificationPanelProps) => {
    const {notifications, isLoading, compact} = props;
    const theme = useTheme();

    if (isLoading) {
        return <LoadingContent isLoading={true}><></></LoadingContent>;
    }

    if (notifications.length === 0) {
        return (
            <Stack alignItems="center" spacing={1} sx={{py: 3}}>
                <NotificationsNone sx={{fontSize: 32, color: theme.palette.text.disabled}}/>
                <Typography variant="body2" color="text.secondary">
                    No notifications
                </Typography>
            </Stack>
        );
    }

    const {today, earlier} = groupNotificationsByTime(notifications);

    return (
        <Stack spacing={0}>
            {today.length > 0 && (
                <>
                    {!compact && (
                        <Typography
                            variant="caption"
                            color="text.disabled"
                            sx={{px: 2, pt: 1.5, pb: 0.5, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5}}
                        >
                            Today
                        </Typography>
                    )}
                    {today.map(notification => (
                        <Fragment key={notification.id}>
                            <NotificationItem
                                notification={notification}
                                onMarkRead={props.onMarkRead}
                                onDelete={props.onDelete}
                                onClose={props.onClose}
                            />
                            <Divider/>
                        </Fragment>
                    ))}
                </>
            )}
            {earlier.length > 0 && (
                <>
                    {!compact && (
                        <Typography
                            variant="caption"
                            color="text.disabled"
                            sx={{px: 2, pt: 1.5, pb: 0.5, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5}}
                        >
                            Earlier
                        </Typography>
                    )}
                    {earlier.map(notification => (
                        <Fragment key={notification.id}>
                            <NotificationItem
                                notification={notification}
                                onMarkRead={props.onMarkRead}
                                onDelete={props.onDelete}
                                onClose={props.onClose}
                            />
                            <Divider/>
                        </Fragment>
                    ))}
                </>
            )}
        </Stack>
    );
};
